import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import { Scale, X } from "lucide-react";

// Lot C (10/2026) — barème PERSONNEL par freelance : chaque champ surcharge le
// barème global (« Barème commissions ») ; laissé vide, il en hérite. Versionné
// et audité ; seules les écritures futures (et les RDV « barème non défini »
// encore en attente) prennent le nouveau taux.
// Endpoints : admin.commissions.freelanceRates / overrideHistory / setOverride /
// clearOverride (SUPER_ADMIN).

type SalesMode = "NONE" | "PERCENT_COLLECTED" | "FIXED_PER_CONVERSION";
type Origin = "GLOBAL" | "PERSONNEL";

const MODE_LABELS: Record<SalesMode, string> = {
  NONE: "Aucune commission",
  PERCENT_COLLECTED: "% de l'encaissé HT",
  FIXED_PER_CONVERSION: "Prime fixe / conversion",
};

const eur = (c: number | null | undefined) =>
  c == null ? "non défini" : (c / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const toCents = (v: string): number | null => {
  if (!v.trim()) return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
const fromCents = (c: number | null | undefined) => (c == null ? "" : String(c / 100).replace(".", ","));

function OriginBadge({ origin }: { origin: Origin }) {
  return origin === "PERSONNEL" ? (
    <Badge variant="warning" className="ml-1">personnel</Badge>
  ) : (
    <span className="ml-1 text-xs text-muted-foreground">global</span>
  );
}

interface Row {
  userId: string;
  name: string;
  email: string;
  staffRole: string | null;
  rates: {
    sdrAmountPerHonoredMeetingCents: number | null;
    salesMode: SalesMode;
    salesPercentBps: number | null;
    salesDurationMonths: number | null;
    salesFixedCents: number | null;
  };
  origin: Record<string, Origin>;
  overrideVersion: number | null;
}

function salesSummary(r: Row["rates"]) {
  if (r.salesMode === "PERCENT_COLLECTED") {
    return `${(r.salesPercentBps ?? 0) / 100} % ${r.salesDurationMonths ? `· ${r.salesDurationMonths} mois` : "· sans limite"}`;
  }
  if (r.salesMode === "FIXED_PER_CONVERSION") return `${eur(r.salesFixedCents)} / conversion`;
  return MODE_LABELS.NONE;
}

function EditModal({ row, onClose }: { row: Row; onClose: () => void }) {
  const utils = trpc.useUtils();
  const history = trpc.admin.commissions.overrideHistory.useQuery({ userId: row.userId });
  const current = history.data?.[0];
  const isSdr = row.staffRole === "FREELANCE_SDR";
  const [sdr, setSdr] = useState(fromCents(current?.sdrAmountPerHonoredMeetingCents));
  const [mode, setMode] = useState<SalesMode | "">((current?.salesMode as SalesMode | null) ?? "");
  const [percent, setPercent] = useState(current?.salesPercentBps == null ? "" : String(current.salesPercentBps / 100));
  const [duration, setDuration] = useState(current?.salesDurationMonths == null ? "" : String(current.salesDurationMonths));
  const [fixed, setFixed] = useState(fromCents(current?.salesFixedCents));
  const [note, setNote] = useState("");
  // Pré-remplit avec la version personnelle en vigueur une fois l'historique chargé.
  useEffect(() => {
    if (!current) return;
    setSdr(fromCents(current.sdrAmountPerHonoredMeetingCents));
    setMode((current.salesMode as SalesMode | null) ?? "");
    setPercent(current.salesPercentBps == null ? "" : String(current.salesPercentBps / 100));
    setDuration(current.salesDurationMonths == null ? "" : String(current.salesDurationMonths));
    setFixed(fromCents(current.salesFixedCents));
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = (msg: string) => {
    toast.success(msg);
    void utils.admin.commissions.freelanceRates.invalidate();
    void utils.admin.commissions.overrideHistory.invalidate();
    onClose();
  };
  const save = trpc.admin.commissions.setOverride.useMutation({
    onSuccess: (r) => done(`Barème personnel v${r.version} enregistré${r.repriced ? ` — ${r.repriced} RDV revalorisé(s)` : ""}.`),
    onError: (e) => toast.error(e.message),
  });
  const clear = trpc.admin.commissions.clearOverride.useMutation({
    onSuccess: () => done("Retour au barème global."),
    onError: (e) => toast.error(e.message),
  });

  const percentBps = percent.trim() ? Math.round(Number(percent.replace(",", ".")) * 100) : null;
  const invalid =
    (sdr.trim() !== "" && toCents(sdr) === null) ||
    (fixed.trim() !== "" && !toCents(fixed)) ||
    (percent.trim() !== "" && (!percentBps || percentBps <= 0 || percentBps > 10_000)) ||
    (duration.trim() !== "" && !(Number(duration) >= 1 && Number(duration) <= 120));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Scale className="h-4 w-4 text-primary" /> Barème personnel — {row.name}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Champ vide = hérite du barème global. Les écritures déjà validées ou payées ne changent jamais.
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (invalid) return;
            save.mutate({
              userId: row.userId,
              sdrAmountPerHonoredMeetingCents: toCents(sdr),
              salesMode: mode || null,
              salesPercentBps: percentBps,
              salesDurationMonths: duration.trim() ? Number(duration) : null,
              salesFixedCents: toCents(fixed),
              note: note.trim() || undefined,
            });
          }}
        >
          {isSdr ? (
            <div className="space-y-1.5">
              <Label htmlFor="ov-sdr">Montant par RDV tenu et validé (€)</Label>
              <Input id="ov-sdr" value={sdr} onChange={(e) => setSdr(e.target.value)} placeholder="hérite du barème global" />
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ov-mode">Mode de commission</Label>
                <select id="ov-mode" value={mode} onChange={(e) => setMode(e.target.value as SalesMode | "")}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Hérite du barème global</option>
                  {(Object.keys(MODE_LABELS) as SalesMode[]).map((k) => <option key={k} value={k}>{MODE_LABELS[k]}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ov-pct">Taux (%)</Label>
                  <Input id="ov-pct" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="hérite" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ov-dur">Durée (mois)</Label>
                  <Input id="ov-dur" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="hérite" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ov-fix">Prime (€)</Label>
                  <Input id="ov-fix" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="hérite" />
                </div>
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="ov-note">Note (motif)</Label>
            <Input id="ov-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex. négocié au contrat du 12/10" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={invalid || save.isLoading}>Enregistrer une version</Button>
            <Button type="button" variant="outline" disabled={!row.overrideVersion || clear.isLoading}
              onClick={() => clear.mutate({ userId: row.userId, note: note.trim() || undefined })}>
              Revenir au global
            </Button>
          </div>
        </form>
        <div>
          <h3 className="text-sm font-medium mb-1">Historique</h3>
          {(history.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucune version personnelle : barème global.</p>
          ) : (
            <ul className="text-xs space-y-1">
              {history.data!.map((h) => (
                <li key={h.id} className="border-b pb-1">
                  <span className="font-medium">v{h.version}</span> · {formatDateTime(h.effectiveFrom)} ·{" "}
                  {isSdr
                    ? `RDV : ${h.sdrAmountPerHonoredMeetingCents == null ? "global" : eur(h.sdrAmountPerHonoredMeetingCents)}`
                    : `mode ${h.salesMode ?? "global"}, ${h.salesPercentBps == null ? "taux global" : `${h.salesPercentBps / 100} %`}, ${h.salesFixedCents == null ? "prime globale" : eur(h.salesFixedCents)}`}
                  {h.note ? ` — ${h.note}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export function FreelanceRatesPanel() {
  const rates = trpc.admin.commissions.freelanceRates.useQuery();
  const [editing, setEditing] = useState<Row | null>(null);
  const items = (rates.data?.items ?? []) as Row[];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="h-4 w-4" /> Taux de commission par freelance
          <span className="text-xs font-normal text-muted-foreground">(barème global v{rates.data?.planVersion ?? 0})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun freelance.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-3">Freelance</th>
                <th className="py-2 pr-3">Taux effectif</th>
                <th className="py-2 pr-3">Version perso.</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.userId} className="border-b last:border-0">
                  <td className="py-2 pr-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.staffRole === "FREELANCE_SDR" ? "SDR" : "Sales"}</div>
                  </td>
                  <td className="py-2 pr-3">
                    {r.staffRole === "FREELANCE_SDR" ? (
                      <>
                        {eur(r.rates.sdrAmountPerHonoredMeetingCents)} / RDV
                        <OriginBadge origin={r.origin.sdrAmountPerHonoredMeetingCents} />
                      </>
                    ) : (
                      <>
                        {salesSummary(r.rates)}
                        <OriginBadge
                          origin={["salesMode", "salesPercentBps", "salesDurationMonths", "salesFixedCents"].some((k) => r.origin[k] === "PERSONNEL") ? "PERSONNEL" : "GLOBAL"}
                        />
                      </>
                    )}
                  </td>
                  <td className="py-2 pr-3">{r.overrideVersion ? `v${r.overrideVersion}` : "—"}</td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Modifier</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {editing && <EditModal row={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}
